import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame } from 'lucide-react';
import InlineEmpty from '../InlineEmpty/InlineEmpty';
import StatusBadge from '../StatusBadge/StatusBadge';
import { formatNumber } from '../../utils/datetime';
import { formatDate, formatDay } from './time';
import { riskFactorText } from './riskFactor';
import { useI18n } from '../../i18n/I18nContext';
import './Student360.css';

const BAND_TONE = { LOW: 'success', MEDIUM: 'warning', HIGH: 'danger' };
const LEDGER_PREVIEW = 12;

function dayClass(day, goal) {
  if (day.frozen) return 'is-frozen';
  if (goal > 0 && day.xp >= goal) return 'is-goal';
  if (day.xp > 0 || day.activities > 0) return 'is-some';
  return '';
}

/**
 * Practice outside the lessons: XP and level, the streak, the last 28 days, counts, the XP ledger.
 * `assessmentHref` (for the accounts that may open it) adds a "Details" link from the AI assessment
 * to the full assessment page of the student.
 */
function PracticeSection({ practice, assessmentHref }) {
  const { t, lang } = useI18n();
  const [showAll, setShowAll] = useState(false);
  const { xp, streak, assessment } = practice;
  const strip = practice.strip || [];
  const counts = practice.counts || {};
  const ledger = practice.ledger || [];
  const shown = showAll ? ledger : ledger.slice(0, LEDGER_PREVIEW);
  // xpForNextLevel is the size of the current level, xpIntoLevel how much of it is behind the student.
  const levelSpan = Number(xp.xpForNextLevel) || 0;
  const levelPercent = levelSpan > 0 ? Math.round((xp.xpIntoLevel / levelSpan) * 100) : 0;
  const toNextLevel = Math.max(0, levelSpan - xp.xpIntoLevel);
  const activeDays = strip.filter((day) => day.xp > 0 || day.activities > 0).length;
  const factor = assessment?.topRiskFactor ? riskFactorText(t, assessment.topRiskFactor) : null;

  return (
    <section className="section-card s360-section" aria-label={t('student360.practice.title')}>
      <div className="s360-tiles">
        <div className="s360-tile">
          <span className="s360-label">{t('student360.practice.level')}</span>
          <span className="s360-tile-value">{xp.level}</span>
          <div className="s360-bar" aria-hidden="true">
            <div className="s360-bar-fill is-some" style={{ width: `${Math.min(100, levelPercent)}%` }} />
          </div>
          <span className="s360-tile-sub">
            {t('student360.practice.xpTotal', { xp: formatNumber(xp.total, { decimals: 0 }) })}
            {levelSpan > 0 ? ` · ${t('student360.practice.xpToNext', { xp: toNextLevel, level: xp.level + 1 })}` : ''}
          </span>
        </div>

        <div className="s360-tile">
          <span className="s360-label">{t('student360.practice.streak')}</span>
          <span className={`s360-tile-value ${streak.current > 0 ? 'is-lit' : ''}`}>
            <Flame size={20} aria-hidden="true" />
            {streak.current}
          </span>
          <span className="s360-tile-sub">
            {t(`student360.practice.streakState.${streak.state}`)}
            {' · '}
            {t('student360.practice.streakBest', { days: streak.longest })}
          </span>
        </div>

        <div className="s360-tile">
          <span className="s360-label">{t('student360.practice.wordsKnown')}</span>
          <span className="s360-tile-value">{counts.wordsKnown}</span>
          <span className="s360-tile-sub">{t('student360.practice.listeningDone', { count: counts.listeningDone })}</span>
        </div>

        <div className="s360-tile">
          <span className="s360-label">{t('student360.practice.games')}</span>
          <span className="s360-tile-value">{counts.gamesPlayed}</span>
          <span className="s360-tile-sub">{t('student360.practice.perfectRounds', { count: counts.perfectRounds })}</span>
        </div>
      </div>

      <div className="s360-group">
        <div className="s360-group-head">
          <h2 className="s360-group-title">{t('student360.practice.last28')}</h2>
          <span className="s360-muted">{t('student360.practice.activeDays', { count: activeDays })}</span>
        </div>
        <div className="s360-strip" role="img" aria-label={`${t('student360.practice.last28')}: ${t('student360.practice.activeDays', { count: activeDays })}`}>
          {strip.map((day) => (
            <span
              key={day.day}
              className={`s360-strip-day ${dayClass(day, xp.dailyGoalXp)}`}
              title={`${formatDay(day.day, lang)} · ${t('student360.practice.dayXp', { xp: day.xp })}`}
            />
          ))}
        </div>
        <p className="s360-strip-legend">
          <span><i className="s360-strip-day is-goal" />{t('student360.practice.legendGoal', { xp: xp.dailyGoalXp })}</span>
          <span><i className="s360-strip-day is-some" />{t('student360.practice.legendSome')}</span>
          <span><i className="s360-strip-day is-frozen" />{t('student360.practice.legendFrozen')}</span>
        </p>
      </div>

      {assessment && (
        <div className="s360-group s360-assessment">
          <div className="s360-group-head">
            <h2 className="s360-group-title">{t('student360.practice.assessment')}</h2>
            {assessmentHref && (
              <Link className="s360-group-link" to={assessmentHref}>{t('student360.practice.assessmentDetails')}</Link>
            )}
          </div>
          <p className="s360-assessment-line">
            <StatusBadge tone={BAND_TONE[assessment.riskBand] || 'neutral'}>
              {t(`student360.riskBand.${assessment.riskBand}`)}
            </StatusBadge>
            <span>{t('student360.practice.assessmentScore', { score: assessment.riskScore })}</span>
            {factor && factor.label && <span className="s360-muted" title={factor.hint || undefined}>{factor.label}</span>}
            <span className="s360-muted">{formatDate(assessment.assessedAt, lang)}</span>
          </p>
        </div>
      )}

      <div className="s360-group">
        <div className="s360-group-head">
          <h2 className="s360-group-title">{t('student360.practice.ledger')}</h2>
        </div>
        {ledger.length === 0 ? (
          <InlineEmpty kind="empty" title={t('student360.practice.noLedger')} />
        ) : (
          <>
            <ul className="s360-list">
              {shown.map((item) => (
                <li key={item.id} className="s360-item">
                  <div className="s360-item__main">
                    <span className="s360-item__title" title={item.title}>{item.title}</span>
                    {item.subtitle && <span className="s360-item__sub">{item.subtitle}</span>}
                  </div>
                  <div className="s360-item__side">
                    <span className="s360-chip">{t(`student360.practice.source.${item.source}`)}</span>
                    {item.xp > 0 && <span className="s360-xp">{`+${item.xp} XP`}</span>}
                    <span className="s360-item__date">{formatDay(item.day, lang)}</span>
                  </div>
                </li>
              ))}
            </ul>
            {ledger.length > LEDGER_PREVIEW && (
              <button type="button" className="s360-toggle" onClick={() => setShowAll((value) => !value)}>
                {showAll
                  ? t('student360.practice.showLess')
                  : t('student360.practice.showMore', { count: ledger.length - LEDGER_PREVIEW })}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default PracticeSection;
